import { type EntityDef, LiftDefSchema } from '@md/shared';
import { createEntity, ENTITY_PHASE_ACTUATOR, type EntityInstance } from './entity.js';

/**
 * Ascenseur / plateforme mobile générique (concept §3.2).
 *
 * Deux régimes, décidés par la présence de `linkedTo` :
 *  - **commandé** : rejoint le dernier point du chemin tant que le signal est
 *    actif, revient au premier sinon ;
 *  - **libre** : parcourt le chemin en boucle, aller-retour.
 *
 * Le déplacement est un pur suivi de cible à vitesse constante : aucune
 * inertie, donc aucune dérive numérique entre deux rejeux (déterminisme).
 * `flags.progress` (0..1) et `flags.target` rendent l'état observable.
 */
export function createLift(def: EntityDef): EntityInstance {
  const cfg = LiftDefSchema.parse(def);
  const path = cfg.path.map(([px, py]) => ({ x: px, y: py }));
  const first = path[0] ?? { x: cfg.x, y: cfg.y };
  const last = path[path.length - 1] ?? first;

  return createEntity({
    id: cfg.id,
    type: 'lift',
    x: first.x,
    y: first.y,
    w: cfg.width,
    h: cfg.height,
    solid: true,
    phase: ENTITY_PHASE_ACTUATOR,
    flags: { target: 1, active: 0 },
    appearance: { shape: 'bar', tint: 0x7f8dbf, activeTint: 0x9ff0ff },
    update(self, ctx) {
      let goal = last;
      if (cfg.linkedTo === undefined) {
        // Libre : aller-retour entre les deux extrémités du chemin.
        const index = self.flags.target ?? 1;
        goal = index === 1 ? last : first;
        if (Math.abs(self.x - goal.x) < 1e-6 && Math.abs(self.y - goal.y) < 1e-6) {
          self.flags.target = index === 1 ? 0 : 1;
        }
      } else {
        const active = ctx.signal(cfg.linkedTo);
        self.flags.active = active ? 1 : 0;
        goal = active ? last : first;
        self.flags.target = active ? 1 : 0;
      }

      const dx = goal.x - self.x;
      const dy = goal.y - self.y;
      const remaining = Math.hypot(dx, dy);
      const stepLength = cfg.speed * ctx.dt;
      if (remaining <= stepLength || remaining === 0) {
        self.vx = 0;
        self.vy = 0;
        self.x = goal.x;
        self.y = goal.y;
        return;
      }
      self.vx = (dx / remaining) * cfg.speed;
      self.vy = (dy / remaining) * cfg.speed;
      self.x += self.vx * ctx.dt;
      self.y += self.vy * ctx.dt;
    },
  });
}
